// src/components/TermsConditions.tsx
import React from "react";
import { Box, Heading, Text, VStack, UnorderedList, ListItem, Link } from "@chakra-ui/react";
import { companyConfig } from "../config/company";

const TermsConditions: React.FC = () => {
  const { operatorDescription, brandName, supportEmail, website, effectiveDate, tradingNames, legalEntityName } = companyConfig;

  // Single source of truth for phase, derived the same way operatorDescription
  // already derives it -- a blank legalEntityName means sole trader / not yet
  // incorporated, non-blank means legalEntityName is the real legal entity
  // and everything else is subordinate to it.
  const isIncorporated = Boolean(legalEntityName);
  // console.log("Is incorporated ", isIncorporated, legalEntityName);

  return (
    <Box maxW="860px" mx="auto" px={6} py={10}>
      <VStack align="start" spacing={6}>

        <Heading size="xl">Terms and Conditions</Heading>
        <Text fontSize="sm" color="gray.500">Effective date: {effectiveDate}</Text>

        {isIncorporated ? (
          <Text>
            These Terms and Conditions ("Terms") govern your access to and use
            of the services operated by {operatorDescription}
            {tradingNames && `, trading as ${tradingNames}`} ("we", "us",
            "our"), including the Eduvic Learning Platform,
            and the Eduvic Practice Platform, accessible at{" "}
            <Link href={website} color="blue.500">{website}</Link> and its
            associated services.
          </Text>
        ) : (
          <Text>
            {operatorDescription} trades as {tradingNames}. These Terms
            govern your access to and use of the services we operate under
            these trading names, including {brandName}, Eduvic Coaching, the
            Eduvic Learning Platform, and the Eduvic Practice Platform,
            accessible at{" "}
            <Link href={website} color="blue.500">{website}</Link> and its
            associated services ("we", "us", "our").
          </Text>
        )}

        <Text>
          By creating an account, engaging our services, or purchasing access to any
          of our platforms, you agree to be bound by these Terms. If you do not
          agree, do not use our services.
        </Text>

      <Heading size="md">1. Our Services</Heading>

      {isIncorporated ? (
        <Text>
          {operatorDescription} operates the following, trading as{" "}
          {tradingNames}:
        </Text>
      ) : (
        <Text>
          {operatorDescription} is a single legal entity, trading as{" "}
          {tradingNames}, offering the following services directly:
        </Text>
      )}

      <UnorderedList spacing={2} pl={4}>
        <ListItem>
          <strong>Eduvic Coaching</strong> — private tutoring and coaching
          services, operated under the trading name Eduvic Coaching.
        </ListItem>
        <ListItem>
          <strong>Eduvic Learning Platform</strong> — our multi-tenant learning
          management and assessment platform, used to deliver enrolment,
          coursework, and end-of-term testing for coaching centres, including
          Eduvic Coaching itself.
        </ListItem>
        <ListItem>
          <strong>Eduvic Practice Platform</strong> — our online practice and
          assessment platform, offering practice tests, mock examinations, and
          preparation materials for Australian selective entry, NAPLAN, ACER,
          EduTest, and related assessments.
        </ListItem>
      </UnorderedList>

      <Text>
        Eduvic Learning Platform and Eduvic Practice Platform are services
        operated directly by {operatorDescription}. These Terms apply to all
        of our services. Where a specific service has additional terms (for
        example, tutoring session policies or platform-specific usage rules),
        those terms will be provided to you separately and apply in addition
        to, not instead of, these Terms. Content and materials provided
        through any of our services are intended for educational preparation
        purposes only and do not guarantee any particular outcome.
      </Text>

        <Heading size="md">2. Eligibility</Heading>
        <Text>
          Our platform is intended for students and their parents or guardians. By
          registering, you confirm that you are either (a) aged 18 or over, or (b) a
          parent or guardian registering on behalf of a minor. You are responsible for
          ensuring that any minor using the platform does so under appropriate supervision.
        </Text>

        <Heading size="md">3. Accounts</Heading>
        <UnorderedList spacing={2} pl={4}>
          <ListItem>You must provide accurate and complete information when creating an account.</ListItem>
          <ListItem>You are responsible for maintaining the confidentiality of your login credentials.</ListItem>
          <ListItem>You must notify us immediately at <Link href={`mailto:${supportEmail}`} color="blue.500">{supportEmail}</Link> if you suspect unauthorised access to your account.</ListItem>
          <ListItem>Each account is for a single user. Account sharing is not permitted.</ListItem>
        </UnorderedList>

        <Heading size="md">4. Purchases and Payment</Heading>
        <Text>
          All prices are displayed in Australian Dollars (AUD) and include GST where
          applicable. Payment is processed securely via Stripe. We do not store your
          card details. Your entitlement to access purchased content begins immediately
          upon successful payment confirmation.
        </Text>
        <Text>
          Access periods are as stated at the time of purchase. We reserve the right to
          update pricing for future purchases; existing purchases are not affected.
        </Text>

        <Heading size="md">5. Refunds</Heading>
        <Text>
          We offer refunds in accordance with our Refund Policy and your rights under
          the Australian Consumer Law. Please refer to our{" "}
          <Link href={`/practice/refund-policy`} color="blue.500">Refund Policy</Link>{" "}
          for full details. Refund requests should be directed to{" "}
          <Link href={`mailto:${supportEmail}`} color="blue.500">{supportEmail}</Link>.
        </Text>

        <Heading size="md">6. Acceptable Use</Heading>
        <Text>
          You agree not to misuse the platform. Prohibited conduct includes but is not
          limited to reproducing, distributing, or sharing our content without authorisation;
          attempting to circumvent access controls; using automated tools to access the
          platform; and engaging in conduct that disrupts other users. Please refer to our{" "}
          <Link href={`/practice/acceptable-use`} color="blue.500">Acceptable Use Policy</Link>{" "}
          for full details.
        </Text>

        <Heading size="md">7. Intellectual Property</Heading>
        <Text>
          All content on the platform, including practice questions, explanations,
          analytics, and interface design, is owned by or licensed to {operatorDescription}.
          You may not reproduce, distribute, or create derivative works from our content
          without our prior written consent. Your licence to access content is personal,
          non-transferable, and limited to the access period purchased.
        </Text>

        <Heading size="md">8. Disclaimer</Heading>
        <Text>
          Our platform is provided "as is." While we take care to ensure content accuracy,
          we do not warrant that content is free from errors or will be continuously
          available. We do not represent that use of our platform will result in any
          particular examination outcome. Nothing in these Terms excludes rights you have
          under the Australian Consumer Law that cannot be excluded.
        </Text>

        <Heading size="md">9. Limitation of Liability</Heading>
        <Text>
          To the extent permitted by law, our liability to you for any loss or damage
          arising from your use of the platform is limited to the amount you paid for
          the relevant purchase. We are not liable for indirect, consequential, or
          incidental losses.
        </Text>

        <Heading size="md">10. Privacy</Heading>
        <Text>
          We collect and handle your personal information in accordance with our{" "}
          <Link href={`/practice/privacy`} color="blue.500">Privacy Policy</Link>{" "}
          and the Privacy Act 1988 (Cth).
        </Text>

        <Heading size="md">11. Changes to Terms</Heading>
        <Text>
          We may update these Terms from time to time. We will notify you of material
          changes by email or by a notice on the platform. Continued use of the platform
          after changes take effect constitutes acceptance of the updated Terms.
        </Text>

        <Heading size="md">12. Governing Law</Heading>
        <Text>
          These Terms are governed by the laws of Victoria, Australia. You submit to
          the non-exclusive jurisdiction of the courts of Victoria.
        </Text>

        <Heading size="md">13. Contact</Heading>
        <Text>
          For questions about these Terms, contact us at{" "}
          <Link href={`mailto:${supportEmail}`} color="blue.500">{supportEmail}</Link>.
        </Text>

      </VStack>
    </Box>
  );
};

export default TermsConditions;